import { AuthError } from '@/types/auth';

/**
 * Parses an error object, typically from an API call (Axios error),
 * and returns a structured AuthError or a standard Error.
 */
export function parseApiError(error: any): AuthError | Error {
  // Handle cases where the server is completely unreachable or request setup fails
  if (error.isAxiosError && !error.response) {
    return new AuthError(
      'Server is unavailable. Please check your connection or try again later.',
      false, // isAccessDenied
      true, // isNetworkError
    );
  }

  if (error.response) {
    const { status, data } = error.response;
    const message = data?.message; // Common place for error messages

    switch (status) {
      // Client Errors (4xx)
      case 400:
        return new Error(
          message ||
            'Bad Request: The server could not understand the request due to invalid syntax.',
        );
      case 401: // Unauthorized
      case 403: // Forbidden
        return new AuthError(
          message ||
            'Access Denied: You do not have permission to access this resource.',
          true,
          false,
        );
      case 404:
        return new Error(
          message || 'Not Found: The requested resource could not be found.',
        );
      case 408:
        return new Error(
          message ||
            'Request Timeout: The server timed out waiting for the request.',
        );
      case 429:
        return new Error(
          message ||
            'Too Many Requests: You have sent too many requests in a given amount of time.',
        );

      // Server Errors (5xx)
      case 500:
        return new AuthError(
          message ||
            'Internal Server Error: Something went wrong on the server.',
          false,
          true, // Treat as a reason to show ErrorState
        );
      case 502:
        return new AuthError(
          message ||
            'Bad Gateway: The server received an invalid response from an upstream server.',
          false,
          true, // Treat as a reason to show ErrorState
        );
      case 503:
        return new AuthError(
          message ||
            'Service Unavailable: The server is currently unable to handle the request. Please try again later.',
          false,
          true, // Treat as a reason to show ErrorState
        );
      case 504:
        return new AuthError(
          message ||
            'Gateway Timeout: The server did not receive a timely response from an upstream server.',
          false,
          true, // Treat as a reason to show ErrorState
        );
      default:
        // For other unhandled status codes with a message
        if (message) {
          return new Error(message);
        }
        break;
    }
  }

  // Fallback for non-HTTP errors, Axios errors without a response but not caught above,
  // or if no specific message is found from the response data.
  return new Error(
    error.message || 'An unknown error occurred. Please try again.',
  );
}
